'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { watchlistApi, Watchlist, Stock } from '@/lib/api/watchlistApi';
import {
  ArrowUpRightIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  ChartBarIcon,
  CheckIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import WatchlistStocksTable from '@/components/WatchlistStocksTable';
import WatchlistNavigation from '@/components/WatchlistNavigation';
import PageLayout from '@/components/layout/PageLayout';
import BackButton from '@/components/ui/BackButton';

interface RecentStock extends Stock {
  watchlist_id: string;
  watchlist_name: string;
  added_at: string;
  added_by?: string;
}

export default function WatchlistPage() {
  const router = useRouter();
  
  const [watchlists, setWatchlists] = useState<Watchlist[]>([]);
  const [filteredWatchlists, setFilteredWatchlists] = useState<Watchlist[]>([]);
  const [userWatchlists, setUserWatchlists] = useState<Watchlist[]>([]);
  const [systemWatchlists, setSystemWatchlists] = useState<Watchlist[]>([]);
  const [view, setView] = useState<'watchlists' | 'common-stocks' | 'intersection-stocks' | 'recent-stocks'>('watchlists');

  const [showCreateWatchlist, setShowCreateWatchlist] = useState(false);
  const [showEditWatchlist, setShowEditWatchlist] = useState<string | null>(null);

  const [watchlistName, setWatchlistName] = useState('');
  const [editWatchlistName, setEditWatchlistName] = useState('');

  const [commonStocks, setCommonStocks] = useState<Stock[]>([]);
  const [intersectionStocks, setIntersectionStocks] = useState<Stock[]>([]);
  const [recentStocks, setRecentStocks] = useState<RecentStock[]>([]);
  const [selectedWatchlistsForIntersection, setSelectedWatchlistsForIntersection] = useState<string[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New state for search and sorting
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'stocks' | 'date'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const openStockDetail = (isin: string) => {
    const detailUrl = `/stocks/detail/${isin}`;
    window.open(detailUrl, '_blank', 'noopener,noreferrer');
  };

  const loadWatchlists = useCallback(async () => {
    setLoading(true);
    try {
      const response = await watchlistApi.listWatchlists();
      setWatchlists(response.watchlists);
      setError(null);
    } catch (err) {
      setError('Failed to load watchlists');
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and sort watchlists by type
  useEffect(() => {
    // Separate watchlists by type
    const defaultWatchlists = watchlists.filter(w => w.watchlist_type === 'default' || !w.watchlist_type);
    const systemGeneratedWatchlists = watchlists.filter(w => w.watchlist_type === 'system_generated');
    
    // Helper function to apply search and sort
    const applyFiltersAndSort = (lists: Watchlist[]) => {
      let filtered = [...lists];
      
      // Apply search filter
      if (searchQuery.trim()) {
        filtered = filtered.filter(watchlist =>
          watchlist.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        let compareValue = 0;
        
        switch (sortBy) {
          case 'name':
            compareValue = a.name.localeCompare(b.name);
            break;
          case 'stocks':
            compareValue = (a.number_of_stocks || 0) - (b.number_of_stocks || 0);
            break;
          case 'date':
            compareValue = new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime();
            break;
        }

        return sortOrder === 'desc' ? -compareValue : compareValue;
      });
      
      return filtered;
    };

    const filteredUserWatchlists = applyFiltersAndSort(defaultWatchlists);
    const filteredSystemWatchlists = applyFiltersAndSort(systemGeneratedWatchlists);
    
    setUserWatchlists(filteredUserWatchlists);
    setSystemWatchlists(filteredSystemWatchlists);
    setFilteredWatchlists([...filteredUserWatchlists, ...filteredSystemWatchlists]);
  }, [watchlists, searchQuery, sortBy, sortOrder]);

  const createWatchlist = async () => {
    if (!watchlistName.trim()) return;
    setLoading(true);
    try {
      await watchlistApi.createWatchlist(watchlistName);
      setWatchlistName('');
      setShowCreateWatchlist(false);
      await loadWatchlists();
      setError(null);
    } catch (err) {
      setError('Failed to create watchlist');
    } finally {
      setLoading(false);
    }
  };

  const updateWatchlist = async (watchlistId: string) => {
    if (!editWatchlistName.trim()) return;
    setLoading(true);
    try {
      await watchlistApi.updateWatchlist(watchlistId, editWatchlistName);
      setEditWatchlistName('');
      setShowEditWatchlist(null);
      await loadWatchlists();
      setError(null);
    } catch (err) {
      setError('Failed to update watchlist');
    } finally {
      setLoading(false);
    }
  };

  const deleteWatchlist = async (watchlistId: string) => {
    if (!confirm('Are you sure you want to delete this watchlist?')) return;
    setLoading(true);
    try {
      await watchlistApi.deleteWatchlist(watchlistId);
      await loadWatchlists();
      setError(null);
    } catch (err) {
      setError('Failed to delete watchlist');
    } finally {
      setLoading(false);
    }
  };

  const loadCommonStocks = async () => {
    setLoading(true);
    try {
      const response = await watchlistApi.getCommonStocks();
      setCommonStocks(response.stocks);
      setView('common-stocks');
      setError(null);
    } catch (err) {
      setError('Failed to load common stocks');
    } finally {
      setLoading(false);
    }
  };

  const loadIntersectionStocks = async () => {
    if (selectedWatchlistsForIntersection.length === 0) return;
    setLoading(true);
    try {
      const response = await watchlistApi.getIntersectionStocks(selectedWatchlistsForIntersection);
      setIntersectionStocks(response.stocks);
      setView('intersection-stocks');
      setError(null);
    } catch (err) {
      setError('Failed to load intersection stocks');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentStocks = async () => {
    setLoading(true);
    try {
      const response = await watchlistApi.getRecentStocks();
      setRecentStocks(response.stocks as RecentStock[]);
      setView('recent-stocks');
      setError(null);
    } catch (err) {
      setError('Failed to load recent stocks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWatchlists();
  }, [loadWatchlists]);

  const totalStocks = watchlists.reduce((sum, w) => sum + (w.number_of_stocks || 0), 0);

  const renderWatchlistCard = (watchlist: Watchlist, isSystem: boolean) => {
    const stockCount = watchlist.number_of_stocks || 0;
    const subtitle = isSystem
      ? 'Curated by DraamView'
      : watchlist.created_at
        ? `Created ${new Date(watchlist.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`
        : 'Created —';

    return (
      <div
        key={watchlist.id}
        role="link"
        tabIndex={0}
        onClick={() => router.push(`/watchlist/${watchlist.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') router.push(`/watchlist/${watchlist.id}`);
        }}
        className="group flex cursor-pointer flex-col justify-between gap-6 border-b border-r border-zinc-200 p-5 transition-colors hover:bg-white focus:outline-none focus-visible:bg-white dark:border-zinc-800 dark:hover:bg-zinc-900 dark:focus-visible:bg-zinc-900"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-display text-2xl leading-tight text-zinc-900 dark:text-white">
              {watchlist.name}
            </h3>
            <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</p>
          </div>
          <div className="-mr-2 -mt-2 flex shrink-0 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100">
            {!isSystem && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowEditWatchlist(watchlist.id);
                  setEditWatchlistName(watchlist.name);
                }}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white"
                aria-label={`Rename ${watchlist.name}`}
                title="Rename"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteWatchlist(watchlist.id);
              }}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 hover:bg-red-50 hover:text-red-600 dark:text-zinc-400 dark:hover:bg-red-500/10 dark:hover:text-red-400"
              aria-label={`Delete ${watchlist.name}`}
              title="Delete"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex items-end justify-between">
          <p className="font-mono text-3xl tabular-nums text-zinc-900 dark:text-white">
            {String(stockCount).padStart(2, '0')}
            <span className="ml-2 font-sans text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
              {stockCount === 1 ? 'stock' : 'stocks'}
            </span>
          </p>
          <ArrowUpRightIcon className="h-4 w-4 text-zinc-400 transition-colors group-hover:text-emerald-700 dark:group-hover:text-emerald-400" />
        </div>
      </div>
    );
  };

  const renderSection = (label: string, lists: Watchlist[], isSystem: boolean) => (
    <section>
      <div className="mb-4 flex items-baseline justify-between border-t border-zinc-900 pt-4 dark:border-zinc-100">
        <h2 className="font-display text-2xl text-zinc-900 dark:text-white">{label}</h2>
        <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">{lists.length}</span>
      </div>
      <div className="grid border-l border-t border-zinc-200 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 dark:border-zinc-800">
        {lists.map((watchlist) => renderWatchlistCard(watchlist, isSystem))}
      </div>
    </section>
  );

  const renderSubviewHeader = (title: string, kicker: string) => (
    <div className="mb-6 flex items-center gap-3">
      <BackButton onClick={() => setView('watchlists')} ariaLabel="Back to watchlists" />
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">{kicker}</p>
        <h2 className="font-display text-3xl text-zinc-900 dark:text-white">{title}</h2>
      </div>
    </div>
  );

  const inputClassName =
    'w-full rounded-md border border-zinc-300 bg-white px-3 py-2.5 text-base text-zinc-900 placeholder:text-zinc-400 sm:text-sm dark:border-zinc-700 dark:bg-zinc-900 dark:text-white';

  const renderNameDialog = (
    title: string,
    value: string,
    onChange: (value: string) => void,
    onCancel: () => void,
    onSubmit: () => void,
    submitLabel: string,
  ) => (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-zinc-950/50 backdrop-blur-sm sm:items-center sm:px-4" onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl border border-zinc-200 bg-zinc-50 max-h-[90dvh] overflow-y-auto p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:rounded-xl dark:border-zinc-800 dark:bg-zinc-900"
      >
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-2xl text-zinc-900 dark:text-white">{title}</h3>
          <button onClick={onCancel} className="-mr-2 inline-flex h-10 w-10 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-200/60 dark:hover:bg-zinc-800" aria-label="Close">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSubmit();
          }}
          placeholder="e.g. Microcap breakouts"
          className={`${inputClassName} mb-5`}
          autoFocus
        />
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 rounded-md border border-zinc-300 py-3 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800">
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={loading || !value.trim()}
            className="flex-1 rounded-md bg-zinc-900 py-3 text-sm font-semibold text-zinc-50 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-emerald-300"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout>
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 md:py-12 lg:px-8">
        <header className="mb-8 flex flex-col gap-5 border-b border-zinc-200 pb-6 sm:flex-row sm:items-end sm:justify-between dark:border-zinc-800">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400">Your desk</p>
            <h1 className="mt-3 text-5xl text-zinc-900 md:text-6xl dark:text-white">Watchlists</h1>
            <p className="mt-2 text-sm text-zinc-500 md:text-base dark:text-zinc-400">
              {searchQuery.trim() ? `${filteredWatchlists.length} of ${watchlists.length}` : watchlists.length} {watchlists.length === 1 ? 'list' : 'lists'} · {totalStocks} stocks tracked
            </p>
          </div>
          <button
            onClick={() => setShowCreateWatchlist(true)}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-5 py-3 text-sm font-semibold text-zinc-50 transition-colors hover:bg-emerald-700 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-emerald-300"
          >
            <PlusIcon className="h-4 w-4" />
            New watchlist
          </button>
        </header>

        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
            {error}
          </div>
        )}

        <WatchlistNavigation
          currentView={view}
          onViewChange={setView}
          onLoadRecentStocks={loadRecentStocks}
          className="mb-6"
        />

        {view === 'watchlists' && (
          <div className="space-y-12">
            {watchlists.length > 0 && (
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
                  <input
                    type="search"
                    placeholder="Search watchlists"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`${inputClassName} pl-9`}
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'stocks' | 'date')}
                    className={`${inputClassName} sm:w-40`}
                    aria-label="Sort by"
                  >
                    <option value="name">Sort: Name</option>
                    <option value="stocks">Sort: Stocks</option>
                    <option value="date">Sort: Date</option>
                  </select>
                  <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="inline-flex h-[46px] w-12 shrink-0 items-center justify-center rounded-md border border-zinc-300 text-zinc-700 hover:bg-zinc-100 sm:h-[42px] dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    aria-label={sortOrder === 'asc' ? 'Sorted ascending' : 'Sorted descending'}
                  >
                    {sortOrder === 'asc' ? <BarsArrowUpIcon className="h-5 w-5" /> : <BarsArrowDownIcon className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}

            {userWatchlists.length > 0 && renderSection('My watchlists', userWatchlists, false)}

            {watchlists.length > 1 && (
              <section className="rounded-xl border border-zinc-200 bg-white p-5 md:p-6 dark:border-zinc-800 dark:bg-zinc-900">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h2 className="font-display text-2xl text-zinc-900 dark:text-white">Compare lists</h2>
                    <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                      Find stocks that appear in every list, or only in the lists you pick.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:flex">
                    <button
                      onClick={loadCommonStocks}
                      className="inline-flex items-center justify-center gap-2 rounded-md border border-zinc-300 px-4 py-2.5 text-sm font-semibold text-zinc-700 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                      <StarSolidIcon className="h-4 w-4 text-amber-500" />
                      In all lists
                    </button>
                    <button
                      onClick={loadIntersectionStocks}
                      disabled={selectedWatchlistsForIntersection.length < 2}
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-zinc-900 px-4 py-2.5 text-sm font-semibold text-zinc-50 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-emerald-300"
                    >
                      <ChartBarIcon className="h-4 w-4" />
                      Intersect ({selectedWatchlistsForIntersection.length})
                    </button>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {watchlists.map((watchlist) => {
                    const isSelected = selectedWatchlistsForIntersection.includes(watchlist.id);
                    return (
                      <button
                        key={watchlist.id}
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() =>
                          setSelectedWatchlistsForIntersection(
                            isSelected
                              ? selectedWatchlistsForIntersection.filter((id) => id !== watchlist.id)
                              : [...selectedWatchlistsForIntersection, watchlist.id],
                          )
                        }
                        className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3.5 py-2 text-sm transition-colors ${
                          isSelected
                            ? 'border-emerald-700 bg-emerald-700 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-zinc-950'
                            : 'border-zinc-300 text-zinc-700 hover:border-zinc-500 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-zinc-500'
                        }`}
                      >
                        {isSelected && <CheckIcon className="h-3.5 w-3.5" />}
                        <span className="truncate">{watchlist.name}</span>
                        <span className="font-mono text-xs opacity-70">{watchlist.number_of_stocks || 0}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {systemWatchlists.length > 0 && renderSection('Curated by DraamView', systemWatchlists, true)}

            {userWatchlists.length === 0 && systemWatchlists.length === 0 && searchQuery.trim() && (
              <div className="rounded-xl border border-dashed border-zinc-300 py-16 text-center dark:border-zinc-700">
                <MagnifyingGlassIcon className="mx-auto h-8 w-8 text-zinc-400" />
                <p className="mt-4 font-display text-2xl text-zinc-900 dark:text-white">Nothing matches &ldquo;{searchQuery}&rdquo;</p>
                <button onClick={() => setSearchQuery('')} className="mt-3 text-sm font-semibold text-emerald-700 hover:underline dark:text-emerald-400">
                  Clear search
                </button>
              </div>
            )}

            {userWatchlists.length === 0 && systemWatchlists.length === 0 && !searchQuery.trim() && watchlists.length === 0 && (
              <div className="rounded-xl border border-dashed border-zinc-300 px-6 py-20 text-center dark:border-zinc-700">
                <p className="font-display text-4xl text-zinc-900 dark:text-white">Start your first list</p>
                <p className="mx-auto mt-3 max-w-sm text-sm text-zinc-500 dark:text-zinc-400">
                  Group the stocks you&apos;re following, then compare lists to see where they overlap.
                </p>
                <button
                  onClick={() => setShowCreateWatchlist(true)}
                  className="mt-6 inline-flex items-center gap-2 rounded-md bg-zinc-900 px-5 py-3 text-sm font-semibold text-zinc-50 hover:bg-emerald-700 dark:bg-zinc-50 dark:text-zinc-900 dark:hover:bg-emerald-300"
                >
                  <PlusIcon className="h-4 w-4" />
                  Create watchlist
                </button>
              </div>
            )}
          </div>
        )}

        {view === 'common-stocks' && (
          <div>
            {renderSubviewHeader('In every list', 'Compare')}
            <WatchlistStocksTable
              stocks={commonStocks}
              loading={loading}
              onOpenInNewWindow={openStockDetail}
              showRemoveAction={false}
              title="common stocks"
              emptyMessage="No common stocks found across all watchlists"
            />
          </div>
        )}

        {view === 'intersection-stocks' && (
          <div>
            {renderSubviewHeader('Intersection', 'Compare')}
            <WatchlistStocksTable
              stocks={intersectionStocks}
              loading={loading}
              onOpenInNewWindow={openStockDetail}
              showRemoveAction={false}
              title="intersection stocks"
              emptyMessage="No common stocks found in selected watchlists"
            />
          </div>
        )}

        {view === 'recent-stocks' && (
          <div>
            {renderSubviewHeader('Recently added', 'Activity')}
            <WatchlistStocksTable
              stocks={recentStocks}
              loading={loading}
              onOpenInNewWindow={openStockDetail}
              showRemoveAction={false}
              title="recent stocks"
              emptyMessage="No recently added stocks found"
            />
          </div>
        )}
      </main>

      {showCreateWatchlist &&
        renderNameDialog(
          'New watchlist',
          watchlistName,
          setWatchlistName,
          () => setShowCreateWatchlist(false),
          createWatchlist,
          loading ? 'Creating…' : 'Create',
        )}

      {showEditWatchlist &&
        renderNameDialog(
          'Rename watchlist',
          editWatchlistName,
          setEditWatchlistName,
          () => setShowEditWatchlist(null),
          () => updateWatchlist(showEditWatchlist),
          loading ? 'Saving…' : 'Save',
        )}
    </PageLayout>
  );
}
